package com.eladfinish.bible.contest.screens.browse.tableOfContents.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SecondaryTabRow
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRowDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.bookmark_not_added_yet
import com.eladfinish.bible.contest.colorPrimaryDark
import com.eladfinish.bible.contest.navigation.BrowseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.CompleteChapterScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.navigation.back.InterceptBackWhen
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsAction
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsEffect
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsStore
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabAction
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabUiState
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabsAction
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabsEffect
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabsStore
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BrowseMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.dialogs.BrowseDialogs
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.common.accentColor
import com.eladfinish.bible.contest.ui.menus.actions.BrowseToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/*
┌──────────────────────────────────────────┐
│                TABS ROW                  │
│  ┌────────────┬════════════════════════┐ │
│  │  CHUMASH   │   ███  BIBLE  ███      │ │
│  └────────────┴════════════════════════┘ │
├──────────────────────────────────────────┤
│               CONTENT AREA               │
│ ┌────────────────────────┬─────────────┐ │
│ │     CHAPTER            │    BOOK     │ │
│ │                        │             │ │
│ │▶ 1. בראשית  │             פרק א׳     │ │
│ │  2. שמות    │             פרק ב׳     │ │
│ │  3. ויקרא   │             פרק ג׳     │ │
│ │  4. במדבר   │             פרק ד׳     │ │
│ │  5. דברים   │             פרק ה׳     │ │
│ │  6. יהושע   │             פרק ו׳     │ │
│ │  7. שופטים  │             פרק ז׳     │ │
│ │                        │             │ │
│ └────────────────────────┴─────────────┘ │
└──────────────────────────────────────────┘
 */

@Composable
fun TabsWithHorizontalPager(
    completeChapter: Boolean,
    fromDialog: Boolean,
    bookIndex: Int = -1,
    chapterIndex: Int = -1,
    verse: Int = 0,
    browseTabsStore: BrowseTabsStore = koinViewModel(
        parameters = {
            parametersOf(
                BrowseTabsStore.BrowseTabsStoreParams(
                    isCompleteChapter = completeChapter,
                    opensReference = fromDialog,
                )
            )
        },
    ),
    browseColumnsStore: BrowseColumnsStore = koinViewModel(parameters = { parametersOf(completeChapter) }),
    toolbarActionBus: ToolbarActionBusViewModel,
    navigator: (NavigationCommand.Navigate) -> Unit,
    logEvent: (String) -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    onNavigateBack: () -> Unit,
) {
    val initialPage = 0

    val currentOnNavigateBack by rememberUpdatedState(onNavigateBack)

    BrowseToolbarBindings(
        completeChapter = completeChapter,
        onAction = browseTabsStore::dispatch,
        toolbarActionBus = toolbarActionBus,
    )

    InterceptBackWhen {
        browseTabsStore.dispatch(BrowseTabsAction.BackClicked)
    }

    val browseState by browseTabsStore.observeState().collectAsState()
    val tabsState by browseColumnsStore.observeState().collectAsState()

    val pagerState = rememberPagerState(
        initialPage = initialPage,
        pageCount = { browseState.tabs.size }
    )

    val scope = rememberCoroutineScope()

    BrowseTabsSideEffectsCollector(
        browseTabsStore = browseTabsStore,
        browseColumnsStore = browseColumnsStore,
        currentOnNavigateBack = currentOnNavigateBack,
        showToastStringResource = showToastStringResource,
        onNavigateToBibleTab = {
            if (pagerState.currentPage == initialPage) {
                onNavigateBack()
            } else {
                scope.launch {
                    pagerState.scrollToPage(initialPage)
                }
            }
        },
    )

    BrowseColumnsSideEffectsCollector(
        browseColumnsStore = browseColumnsStore,
        navigator = navigator,
    )

    LaunchedEffect(fromDialog, bookIndex, chapterIndex, verse) {
        if (fromDialog) {
            browseColumnsStore.dispatch(
                BrowseColumnsAction.OpenReference(
                    bookIndex = bookIndex,
                    chapterIndex = chapterIndex,
                    verse = verse,
                )
            )
        }
    }

    TabsWithHorizontalPagerContent(
        pagerState = pagerState,
        tabs = browseState.tabs,
        tabStates = tabsState.tabStates,
        onTabAction = { mode, action ->
            browseColumnsStore.dispatch(
                BrowseColumnsAction.TabAction(
                    mode = mode,
                    action = action,
                )
            )
        },
        logEvent = logEvent,
    )

    SetScreenIsLoading(browseState.isLoading || tabsState.isLoading)

    BrowseDialogs(
        dialogState = browseState.dialogState,
        onAction = browseTabsStore::dispatch
    )
}

@Composable
private fun BrowseTabsSideEffectsCollector(
    browseTabsStore: BrowseTabsStore,
    browseColumnsStore: BrowseColumnsStore,
    currentOnNavigateBack: () -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    onNavigateToBibleTab: () -> Unit,
) {
    LaunchedEffect(Unit) {
        browseTabsStore.observeSideEffect().collect {
            when (it) {
                BrowseTabsEffect.NavigateBack -> {
                    currentOnNavigateBack()
                }

                BrowseTabsEffect.NotifyNoBookmark -> {
                    showToastStringResource(SharedRes.strings.bookmark_not_added_yet)
                }

                is BrowseTabsEffect.HandleBookmark -> {
                    browseColumnsStore.dispatch(BrowseColumnsAction.OpenBookmark(bookmarkData = it.bookmarkData))
                }

                is BrowseTabsEffect.HandleRemoteChapterCompletionSelected -> {
                    browseColumnsStore.dispatch(
                        BrowseColumnsAction.OpenRemoteChapterCompletion(
                            bookIndex = it.bookIndex,
                            chapterIndex = it.chapterIndex,
                            completionDetails = it.completionDetails,
                        )
                    )
                }

                BrowseTabsEffect.NavigateToBibleTab -> {
                    onNavigateToBibleTab()
                }
            }
        }
    }
}

@Composable
private fun BrowseColumnsSideEffectsCollector(
    browseColumnsStore: BrowseColumnsStore,
    navigator: (NavigationCommand.Navigate) -> Unit,
) {
    LaunchedEffect(Unit) {
        browseColumnsStore.observeSideEffect().collect { effect ->
            when (effect) {
                is BrowseColumnsEffect.OpenContent -> {
                    navigator(effect.navigation)
                }
            }
        }
    }
}

@Composable
internal fun TabsWithHorizontalPagerContent(
    pagerState: PagerState,
    tabs: ImmutableList<StringDesc>,
    tabStates: List<BrowseTabUiState>,
    onTabAction: (BrowseMode, BrowseTabAction) -> Unit,
    logEvent: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()

    val selectedTabIndex by remember { derivedStateOf { pagerState.currentPage } }

    val tabEvents = listOf("BibleTab", "ChumashTab", "SederTab", "ShonotTab").toImmutableList()

    // Saved, so that a rotation does not report the tab as opened again.
    var lastLoggedTabIndex by rememberSaveable { mutableStateOf(-1) }
    LaunchedEffect(selectedTabIndex) {
        if (lastLoggedTabIndex != selectedTabIndex) {
            lastLoggedTabIndex = selectedTabIndex
            logEvent(tabEvents[selectedTabIndex])
        }
    }

    Column(modifier = Modifier.fillMaxSize()) {
        SecondaryTabRow(
            selectedTabIndex = selectedTabIndex,
            modifier = Modifier.fillMaxWidth(),
            containerColor = colorResource(SharedRes.colors.colorPrimaryDark),
            indicator = {
                TabRowDefaults.SecondaryIndicator(
                    modifier = Modifier.tabIndicatorOffset(selectedTabIndex),
                    color = accentColor,
                )
            },
        ) {
            tabs.forEachIndexed { index, currentTab ->
                Tab(
                    selected = selectedTabIndex == index,
                    selectedContentColor = MaterialTheme.colorScheme.primary,
                    unselectedContentColor = MaterialTheme.colorScheme.outline,
                    onClick = {
                        scope.launch { pagerState.animateScrollToPage(index) }
                    },
                    text = {
                        Text(
                            text = currentTab.localized(),
                            color = if (selectedTabIndex == index) accentColor else Color.White,
                        )
                    },
                )
            }
        }

        HorizontalPager(
            state = pagerState,
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
        ) { page ->
            val mode = BrowseMode.entries[page]
            val tabState = tabStates.getOrNull(index = page)
            tabState?.let {
                BrowseTabRoot(
                    browseTabUiState = it,
                    browseTabActionDispatcher = { action ->
                        onTabAction(mode, action)
                    }
                )
            }
        }
    }
}

@Composable
private fun BrowseToolbarBindings(
    completeChapter: Boolean,
    onAction: (BrowseTabsAction.ToolbarClicked) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
) {
    val screenKeys = setOf(
        BrowseScreenNavigationDestination::class.screenKey,
        CompleteChapterScreenNavigationDestination::class.screenKey,
    )
    val activeScreenKey = if (completeChapter) {
        CompleteChapterScreenNavigationDestination::class.screenKey
    } else {
        BrowseScreenNavigationDestination::class.screenKey
    }

    val onDispatch by rememberUpdatedState<(BrowseToolbarAction) -> Unit> { action ->
        onAction(BrowseTabsAction.ToolbarClicked(toolbarAction = action))
    }

    LaunchedEffect(activeScreenKey) {
        toolbarActionBus.setActiveScreenKey(activeScreenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey !in screenKeys) return@collect
            val action = event.action as? BrowseToolbarAction ?: return@collect
            onDispatch(action)
        }
    }
}
